import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import type { RejectSuggestionExperience } from '../experiences/reject-suggestion-experience';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import {
	addDiscardAllAiResultDiscardedUnifiedAnalytics,
	addDiscardAllSuggestionsAnalytics,
} from '../ui/utils/analytics';
import { updateSuggestionStatus } from '../ui/utils/api';
import type { DiscardAllSuggestionsAction } from './types';

export const discardAllSuggestionsAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	rejectSuggestionExperience?: RejectSuggestionExperience,
) => DiscardAllSuggestionsAction =
	(api, rejectSuggestionExperience) =>
	(ids): boolean => {
		const experience = rejectSuggestionExperience?.start('discardAll');
		try {
			const state = api?.aiSuggestions?.sharedState.currentState();
			const suggestionById = state?.suggestionById;
			if (!suggestionById || suggestionById.size === 0) {
				experience?.failure({ reason: 'suggestionNotFound' });
				return false;
			}

			const requestedIds = ids === undefined ? undefined : new Set(ids);
			const suggestions = Array.from(suggestionById.values()).filter(
				({ id }) => requestedIds === undefined || requestedIds.has(id),
			);
			if (suggestions.length === 0) {
				experience?.failure({ reason: 'suggestionNotFound' });
				return false;
			}
			const discardedIds = suggestions.map(({ id }) => id);

			const dispatched = api?.core?.actions.execute(({ tr }) => {
				addDiscardAllSuggestionsAnalytics(api?.analytics?.actions)(tr, suggestions);
				addDiscardAllAiResultDiscardedUnifiedAnalytics(api?.analytics?.actions)(tr, suggestions);
				tr.setMeta(
					aiSuggestionsPluginKey,
					requestedIds === undefined
						? { action: 'CLEAR_SUGGESTIONS' }
						: { action: 'REMOVE_SUGGESTIONS', ids: discardedIds },
				);
				api?.showDiff?.commands.hideDiff?.({ tr });
				return tr;
			});

			if (
				!dispatched ||
				discardedIds.some((id) => rejectSuggestionExperience?.isRejected(id) === false)
			) {
				experience?.failure({ reason: 'rejectTransactionNotApplied' });
			}

			const updates = discardedIds.map((id) => ({ id, status: 'DISMISSED' as const }));

			updateSuggestionStatus({ updates }).then(
				() => experience?.success(),
				() => experience?.failure({ reason: 'updateStatusFailed' }),
			);

			return true;
		} catch (error) {
			experience?.failure({ reason: 'rejectSuggestionError' });
			throw error;
		}
	};
